import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { workerPublicProfileResponseSchema } from "@src/appV2/redesign/WorkWithFriends/hooks/useGetWorkerPublicProfile";
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";

const nonBookableCriteriaResponseWithMetadata = z.object({
  code: z.string().optional(),
  message: z.string().optional(),
  metadata: z.record(z.unknown()).optional(),
});

const bookabilityResultDto = z.object({
  decision: z.enum(["ALLOWED", "BLOCKED", "UNDETERMINED"]),
  unmetCriteria: z.array(z.string()).optional(),
  unmetCriteriaWithMetadata: z.array(nonBookableCriteriaResponseWithMetadata).optional(),
});

export const getFriendEligibilitiesResponseSchema = z.object({
  meta: z
    .object({
      totalFriendsCount: z.number(),
      eligibleFriendsCount: z.number(),
    })
    .optional(),
  data: z.array(
    z.object({
      ...workerPublicProfileResponseSchema.shape,
      attributes: z.object({
        ...workerPublicProfileResponseSchema.shape.attributes.shape,
        eligible: z.boolean(),
        bookabilityResult: bookabilityResultDto.optional(),
      }),
    })
  ),
});
export type GetFriendEligibilitiesResponse = z.infer<typeof getFriendEligibilitiesResponseSchema>;

export const getFriendEligibilitiesRequestSchema = z.object({
  filter: z
    .object({
      refereeId: z.string().optional(),
    })
    .optional(),
});
export type GetFriendEligibilitiesPayload = z.infer<typeof getFriendEligibilitiesRequestSchema>;

export function useGetFriendEligibilities(
  shiftId: string,
  payload?: GetFriendEligibilitiesPayload
) {
  return useQuery({
    queryKey: ["friendEligibilities", shiftId],
    queryFn: async () => {
      const response = await get({
        url: `${environmentConfig.REACT_APP_WORKER_APP_BFF_URL}/shifts/${shiftId}/friend-eligibilities`,
        queryParams: { filter: payload },
        requestSchema: getFriendEligibilitiesRequestSchema,
        responseSchema: getFriendEligibilitiesResponseSchema,
      });

      return response.data;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_SHIFT_TRANSFER_ELIGIBLE_FRIENDS_FAILURE,
    },
  });
}
